import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { CHAIN_ID, SIGNING_ADMINS } from '../../constants/constants';
import {
  getAccounts,
  getChainId,
  normalizeChainId,
  requestAccounts,
  revokeAccountsPermission,
  switchToTargetChain,
} from '../../utils/walletRequests';
import {
  Eip1193Provider,
  setActiveProvider,
  waitForInjectedProvider,
} from '../../utils/walletProvider';
import {
  connectWalletConnect,
  disconnectWalletConnect,
  restoreWalletConnect,
  WalletConnectProvider,
} from '../../utils/walletConnect';

/**
 * Единственное место, где живёт состояние подключения.
 *
 * Раньше оно было заперто внутри кнопки, а подпись шла через `window.ethereum`
 * напрямую: транзакцию можно было отправить, не нажимая «Подключить», и никто
 * не проверял сеть. Здесь же — подписка на смену аккаунта и сети, чтобы
 * показанный адрес совпадал с тем, от чьего имени уйдёт подпись.
 *
 * Кошельков два вида: расширение и WalletConnect (телефон или QR). Найденное
 * расширение (`injected`) и кошелёк, через который подключились (`wallet`), —
 * разные вещи: на компьютере с MetaMask можно подключиться и по QR.
 */

export type WalletKind = 'injected' | 'walletconnect';

export interface WalletState {
  hasInjected: boolean;
  walletKind: WalletKind | null;
  detecting: boolean;
  address: string;
  isConnected: boolean;
  isCorrectChain: boolean;
  /** Адрес из списка тех, кому бэк разрешает менять данные. */
  isKnownAdmin: boolean;
  /** Подключён и в правильной сети — можно подписывать. */
  isReady: boolean;
  busy: boolean;
  error: string;
  connect: (via?: WalletKind) => Promise<void>;
  switchChain: () => Promise<void>;
  disconnect: () => Promise<void>;
}

const WalletContext = createContext<WalletState | null>(null);

const KNOWN_ADMINS = new Set(
  SIGNING_ADMINS.map((address) => address.toLowerCase()),
);

export const useWallet = (): WalletState => {
  const value = useContext(WalletContext);

  if (!value) {
    throw new Error('useWallet вызван вне WalletProvider');
  }

  return value;
};

export const WalletProvider = ({ children }: { children: ReactNode }) => {
  const [injected, setInjected] = useState<Eip1193Provider | null>(null);
  const [wallet, setWallet] = useState<Eip1193Provider | null>(null);
  const [kind, setKind] = useState<WalletKind | null>(null);
  const [detecting, setDetecting] = useState(true);
  const [address, setAddress] = useState('');
  const [chainId, setChainId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Поиск кошелька и восстановление уже выданного доступа.
  useEffect(() => {
    let cancelled = false;

    const detect = async () => {
      const [provider, walletConnect] = await Promise.all([
        waitForInjectedProvider(),
        restoreWalletConnect(),
      ]);

      if (cancelled) return;

      setInjected(provider);
      setDetecting(false);

      if (walletConnect) {
        setWallet(walletConnect);
        setKind('walletconnect');
        setAddress(walletConnect.accounts[0] ?? '');
        setChainId(await getChainId(walletConnect));
        return;
      }

      if (!provider) return;

      // Разрешение в расширении переживает перезагрузку страницы —
      // спрашивать его заново незачем.
      const [accounts, currentChainId] = await Promise.all([
        getAccounts(provider),
        getChainId(provider),
      ]);

      if (cancelled) return;

      setAddress(accounts[0] ?? '');
      setChainId(currentChainId);

      if (accounts.length > 0) {
        setWallet(provider);
        setKind('injected');
      }
    };

    detect();

    return () => {
      cancelled = true;
    };
  }, []);

  // Подпись берёт кошелёк из модуля, а не из контекста: она живёт в утилитах.
  useEffect(() => {
    setActiveProvider(wallet);
  }, [wallet]);

  // Смена аккаунта или сети прямо в кошельке. Пока ничего не подключено,
  // слушаем расширение: доступ могли выдать из него самого.
  const source = wallet ?? injected;

  useEffect(() => {
    if (!source?.on) return undefined;

    const handleAccountsChanged = (accounts: string[]) => {
      setAddress(accounts?.[0] ?? '');
      setError('');

      if (accounts?.length > 0 && !wallet) {
        setWallet(source);
        setKind('injected');
      }
    };

    const handleChainChanged = (nextChainId: string) => {
      setChainId(normalizeChainId(nextChainId));
      setError('');
    };

    // У WalletConnect это разрыв сессии из приложения кошелька.
    const handleDisconnect = () => {
      setAddress('');

      if (kind === 'walletconnect') {
        setWallet(null);
        setKind(null);
      }
    };

    source.on('accountsChanged', handleAccountsChanged);
    source.on('chainChanged', handleChainChanged);
    source.on('disconnect', handleDisconnect);

    return () => {
      source.removeListener?.('accountsChanged', handleAccountsChanged);
      source.removeListener?.('chainChanged', handleChainChanged);
      source.removeListener?.('disconnect', handleDisconnect);
    };
  }, [kind, source, wallet]);

  const connect = useCallback(
    async (via: WalletKind = 'injected') => {
      // Второй клик, пока открыт запрос, кошелёк отбрасывает с -32002.
      if (busy) return;

      setError('');

      if (via === 'injected' && !injected) {
        setError(
          'Расширение кошелька не найдено. Подключитесь через WalletConnect.',
        );
        return;
      }

      setBusy(true);

      try {
        let provider: Eip1193Provider;
        let accounts: string[];

        if (via === 'walletconnect') {
          const walletConnect = await connectWalletConnect();
          provider = walletConnect;
          accounts = walletConnect.accounts;
        } else {
          provider = injected as Eip1193Provider;
          accounts = await requestAccounts(provider);
        }

        if (!accounts?.length) {
          throw new Error('Кошелёк не вернул ни одного аккаунта');
        }

        setWallet(provider);
        setKind(via);
        setAddress(accounts[0]);

        const currentChainId = await getChainId(provider);
        setChainId(currentChainId);

        if (currentChainId !== CHAIN_ID) {
          setChainId(await switchToTargetChain(provider));
        }
      } catch (connectError) {
        setError((connectError as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [busy, injected],
  );

  const switchChain = useCallback(async () => {
    if (busy || !wallet) return;

    setError('');
    setBusy(true);

    try {
      setChainId(await switchToTargetChain(wallet));
    } catch (switchError) {
      setError((switchError as Error).message);
    } finally {
      setBusy(false);
    }
  }, [busy, wallet]);

  const disconnect = useCallback(async () => {
    setError('');

    // Локального сброса мало: кошелёк продолжит считать сайт разрешённым.
    // Расширение просим отозвать доступ, у WalletConnect рвём сессию.
    if (kind === 'walletconnect' && wallet) {
      await disconnectWalletConnect(wallet as WalletConnectProvider);
    } else if (wallet) {
      await revokeAccountsPermission(wallet);
    }

    setWallet(null);
    setKind(null);
    setAddress('');
  }, [kind, wallet]);

  const value = useMemo<WalletState>(() => {
    const isCorrectChain = chainId === CHAIN_ID;

    return {
      hasInjected: Boolean(injected),
      walletKind: kind,
      detecting,
      address,
      isConnected: Boolean(address),
      isCorrectChain,
      isKnownAdmin: Boolean(address) && KNOWN_ADMINS.has(address.toLowerCase()),
      isReady: Boolean(address) && Boolean(wallet) && isCorrectChain,
      busy,
      error,
      connect,
      switchChain,
      disconnect,
    };
  }, [
    address,
    busy,
    chainId,
    connect,
    detecting,
    disconnect,
    error,
    injected,
    kind,
    switchChain,
    wallet,
  ]);

  return (
    <WalletContext.Provider value={value}>{children}</WalletContext.Provider>
  );
};

export default WalletProvider;
